import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { useMemo } from "react"
import { useIntl } from "react-intl"

export interface UseNumberFormattingUtilsParams {
	type: "number" | "currency"
	unit?: string
	unitPosition?: "prefix" | "suffix"
}

export const useNumberFormattingUtils = ({
	type,
	unit,
	unitPosition,
}: UseNumberFormattingUtilsParams): {
	decimalSeparator: string
	formatNumber: (value: number | undefined) => string
	parseNumber: (value: string) => number | undefined
	resolvedUnit: string | undefined
	resolvedUnitPosition: "prefix" | "suffix" | undefined
	thousandSeparator: string
} => {
	const { formatNumber: formatNumberIntl, formatNumberToParts } = useIntl()

	const { currency } = useDealerContext()

	return useMemo(() => {
		const parts = formatNumberToParts(
			10000.1,
			type === "currency" ? { currency: unit ?? currency, style: "currency" } : undefined,
		)

		const decimalSeparator = parts.find((p) => p.type === "decimal")?.value ?? "."
		const thousandSeparator = parts.find((p) => p.type === "group")?.value ?? ","

		const parseNumber = (value: string) => {
			const parsedValue = Number.parseFloat(
				value
					.replace(/[^\d.,]/gs, "")
					.replace(new RegExp(`\\${thousandSeparator}`, "g"), "")
					.replace(new RegExp(`\\${decimalSeparator}`), "."),
			)
			return Number.isNaN(parsedValue) ? undefined : parsedValue
		}

		const formatNumber = (val: number | undefined): string => {
			if (val === undefined || val === null) {
				return ""
			}
			if (type === "currency") {
				return formatNumberIntl(val, {
					maximumFractionDigits: 2,
					minimumFractionDigits: 2,
				})
			}
			return formatNumberIntl(val, {
				maximumFractionDigits: 0,
			})
		}

		let resolvedUnit: string | undefined = unit
		let resolvedUnitPosition: "prefix" | "suffix" | undefined = unitPosition

		if (type === "currency") {
			for (let index = 0; index < parts.length; index++) {
				const part = parts[index]
				if (part.type === "currency") {
					resolvedUnit = part.value
					resolvedUnitPosition = unitPosition ?? (index === 0 ? "prefix" : "suffix")
					break
				}
			}
		}

		return {
			decimalSeparator,
			formatNumber,
			parseNumber,
			resolvedUnit,
			resolvedUnitPosition,
			thousandSeparator,
		}
	}, [formatNumberIntl, formatNumberToParts, type, currency, unit, unitPosition])
}
